import { isNotNil } from 'es-toolkit';

import { StatusLabel } from '@clearverify/constants';
import type { Nullable } from '@clearverify/types';

import { QueryMode, User, UserStatus, UserWhere } from 'src/api';
import { UserPermissionKey, UserPermissions } from 'src/constants/user';

export const getUserFullName = (
  user?: Nullable<Partial<Pick<User, 'firstName' | 'lastName' | 'middleName'>>>,
) => {
  if (!user) return null;
  return [user.firstName, user.middleName, user.lastName].filter(isNotNil).join(' ').trim();
};

export const getUserFullNameFilter = (
  searchValue: Nullable<string>,
  isOptional: boolean | undefined = false,
  isEquals: boolean | undefined = false,
): UserWhere => {
  if (!searchValue) return {};

  const filterFullName: string = isOptional ? 'OR' : 'AND';
  const filterMethod: string = isEquals ? 'equals' : 'contains';

  return searchValue
    .split(' ')
    .filter(isNotNil)
    .reduce<UserWhere>(
      (filter, searchValue) => {
        return {
          [filterFullName]: [
            ...(filter.OR || []),
            ...(filter.AND || []),
            {
              OR: [
                {
                  firstName: {
                    [filterMethod]: searchValue,
                    mode: QueryMode.Insensitive,
                  },
                },
                {
                  lastName: {
                    [filterMethod]: searchValue,
                    mode: QueryMode.Insensitive,
                  },
                },
                {
                  middleName: {
                    [filterMethod]: searchValue,
                    mode: QueryMode.Insensitive,
                  },
                },
              ],
            },
          ],
        };
      },
      { AND: [] },
    );
};

export const formatUserStatus = (status: Nullable<UserStatus>, dataIsDeleted?: boolean) => {
  if (dataIsDeleted) return StatusLabel.DataDeleted;

  switch (status) {
    case UserStatus.Active:
      return StatusLabel.Active;
    case UserStatus.Inactive:
      return StatusLabel.Inactive;
    case UserStatus.Pending:
      return StatusLabel.Pending;

    default:
      return StatusLabel.Inactive;
  }
};

export const isUserPermissionsKey = (objectKey: string): objectKey is keyof UserPermissions => {
  return objectKey in UserPermissionKey;
};

export const isEnableStatus = (status: Nullable<UserStatus>) => {
  return isNotNil(status) && (status === UserStatus.Active || status === UserStatus.Inactive);
};
